import { z } from 'zod'
import { ALL_USER_PERMISSIONS } from 'api/admin/userPermissions/permissions'

const permissionSchema = z.enum(ALL_USER_PERMISSIONS)

export const permissionGrantFormSchema = z
  .object({
    permission: permissionSchema,
    effectiveAt: z.string().min(1, 'Effective at is required'),
    expiresAt: z.string().nullable(),
  })
  .superRefine((value, ctx) => {
    if (value.expiresAt !== null && value.expiresAt <= value.effectiveAt) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Expires at must be after effective at',
        path: ['expiresAt'],
      })
    }
  })

export const companyScopeGrantFormSchema = z
  .object({
    scopeType: z.enum(['all_companies', 'company', 'company_group']),
    companyId: z.number().nullable(),
    companyName: z.string().nullable(),
    companyGroupId: z.number().nullable(),
    companyGroupName: z.string().nullable(),
    effectiveAt: z.string().min(1, 'Effective at is required'),
    expiresAt: z.string().nullable(),
  })
  .superRefine((value, ctx) => {
    if (value.scopeType === 'company' && value.companyId == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Company is required',
        path: ['companyId'],
      })
    }
    if (value.scopeType === 'company_group' && value.companyGroupId == null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Company group is required',
        path: ['companyGroupId'],
      })
    }
    if (value.expiresAt !== null && value.expiresAt <= value.effectiveAt) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Expires at must be after effective at',
        path: ['expiresAt'],
      })
    }
  })

export const userPermissionsFormSchema = z
  .object({
    userId: z.number({
      required_error: 'User is required',
      invalid_type_error: 'User is required',
    }),
    name: z.string().min(1, 'Name is required'),
    email: z.string(),
    permissions: z.array(permissionGrantFormSchema),
    companyScopes: z.array(companyScopeGrantFormSchema),
  })
  .superRefine((value, ctx) => {
    if (value.permissions.length === 0 && value.companyScopes.length === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Select at least one permission or company scope',
        path: ['permissions'],
      })
    }
  })

export const authorizeAdminFormSchema = z
  .object({
    userId: z.number({
      required_error: 'User is required',
    }),
    name: z.string().min(1, 'Name is required'),
    email: z.string().email(),
  })
  .refine(
    (value) => value.email.toLowerCase().trim().endsWith('@workwhilejobs.com'),
    {
      message: 'User must have a @workwhilejobs.com email address',
      path: ['userId'],
    }
  )

export type PermissionGrantFormSchema = z.infer<
  typeof permissionGrantFormSchema
>
export type CompanyScopeGrantFormSchema = z.infer<
  typeof companyScopeGrantFormSchema
>
export type UserPermissionsFormSchema = z.infer<
  typeof userPermissionsFormSchema
>
export type AuthorizeAdminFormSchema = z.infer<typeof authorizeAdminFormSchema>
